/* eslint-disable @typescript-eslint/no-explicit-any */
import { CMS_TOKEN, CMS_URL, MEDIA_API_BASE_URL } from "@/lib/constants";
import { Log } from "@/lib/logger";
import {
	MediaCategory,
	MediaPost,
	MediaPosts,
	MediaQueryParamValue,
	MediaQueryParams,
	MediaRegion,
	MediaTag,
} from "@/types/media";

/**
 * Utility function to transform raw API data to MediaPost format
 */
const transformToMediaPost = (post: any): MediaPost => {
	try {
		const date_updated = new Date(post?.date_updated).toLocaleDateString();
		const date_created = new Date(post?.date_created).toLocaleDateString();
		const authorName = post?.author?.name || "Unknown Author";
		const thumbnailId = post?.featured_media?.id || post?.featured_media;
		const thumbnailUrl = thumbnailId
			? `${CMS_URL}/assets/${thumbnailId}`
			: "/news/default-blog-post-placeholder.png";

		const regions = Array.isArray(post?.regions)
			? post.regions.map((regionObj: any) => ({
					id: regionObj?.Regions_id?.id,
					name: regionObj?.Regions_id?.name,
					slug: regionObj?.Regions_id?.slug,
			  }))
			: [];

		const transformedPost = {
			id: post?.id,
			date_updated: date_updated,
			date_created: date_created,
			slug: post?.slug,
			status: post?.status || "publish",
			type: post?.type || "blog",
			content: post?.content || "",
			title: post?.title,
			featured_media: thumbnailUrl || "",
			news_landing_page__featured_posts__fk: post?.news_landing_page__featured_posts__fk || null,
			homepage__stories__fk: post?.homepage__stories__fk || null,
			news_landing_page_popular_week_fk: post?.news_landing_page_popular_week_fk || null,
			proptech_news_section_fk: post?.proptech_news_section_fk || null,
			tags: post?.tags || [],
			posts_categories: post?.posts_categories || [],
			regions: regions || [],
			author: {
				id: post?.author?.id || 0,
				user_created: post?.author?.user_created || "",
				date_created: post?.author?.date_created || "",
				user_updated: post?.author?.user_updated || "",
				date_updated: post?.author?.date_updated || "",
				name: post?.author?.name || "Unknown Author",
				image: post?.author?.image || null,
			},
			date_gmt: date_updated,
			modified: post?.modified,
			modified_gmt: post?.modified_gmt,
			categories: post?.post_categories || [],
			authorName: authorName,
			thumbnailUrl: thumbnailUrl,
			seo_title: post?.seo?.title || "",
			seo_description: post?.seo?.meta_description || "",
			is_premium_post: post?.is_premium_post || false,
			is_featured_post_one: post?.is_featured_post_one || false,
			is_featured_post_two: post?.is_featured_post_two || false,
			is_featured_post_three: post?.is_featured_post_three || false,
		};

		Log.d(`Successfully transformed post: ${post?.title || "Unknown Title"}`, { event: "general" });
		return transformedPost;
	} catch (error) {
		Log.e(`Error transforming post data: ${error}`, { event: "general" });
		throw error;
	}
};

export const getPostBySlug = async (postSlug: string): Promise<MediaPost | null> => {
	try {
		const encodedSlug = encodeURIComponent(postSlug);
		const directusResponse = await fetch(
			`${CMS_URL}/items/Posts?filter[slug][_eq]=${encodedSlug}&fields=*,posts_categories.*,author.*,post_tags.Tags_id.*,regions.Regions_id.*,seo.*`,
			{
				method: "GET",
				headers: {
					Authorization: `Bearer ${CMS_TOKEN}`,
					"Content-Type": "application/json",
				},
			}
		);

		if (!directusResponse.ok) {
			const errorData = await directusResponse.json();
			console.error("Directus API Error:", errorData);
			Log.e(
				`Directus API Error for slug ${postSlug}: ${directusResponse.status} - ${JSON.stringify(
					errorData
				)}`,
				{ event: "general" }
			);
			throw new Error(
				`Directus API returned ${directusResponse.status}: ${JSON.stringify(errorData)}`
			);
		}

		const response = await directusResponse.json();
		if (!response?.data || response.data.length === 0) {
			console.log("No post found with the provided slug.");
			return null;
		}

		const post = response?.data?.[0];
		console.log("post: ", response);

		const transformedPost = transformToMediaPost(post);
		Log.i(`Successfully retrieved post by slug: ${postSlug}`, { event: "general" });
		return transformedPost;
	} catch (error) {
		console.error("Error in getPostBySlug:", error);
		return null;
	}
};

export const getPodcasts = async () => {
	try {
		const res = await fetch(`${CMS_URL}/items/news_landing_page?fields=*,podcast.*`, {
			headers: {
				Authorization: `Bearer ${CMS_TOKEN}`,
				"Content-Type": "application/json",
			},
			next: { revalidate: 60 },
		});

		const response = await res.json();
		if (!res.ok) {
			Log.e(`Failed to fetch podcasts: ${res.status} ${res.statusText}`, { event: "general" });
			throw new Error("Failed to fetch podcasts from Directus");
		}
		const podcasts = response?.data?.podcast || [];
		Log.i(`Successfully fetched ${podcasts.length} podcasts`, { event: "general" });
		return podcasts;
	} catch (error) {
		Log.e(`Error fetching podcasts: ${error}`, { event: "general" });
		return [];
	}
};

export const getProptechNews = async (): Promise<{
	posts: MediaPosts;
}> => {
	try {
		const apiUrl = `${CMS_URL}/items/news_landing_page?fields=*,proptech_news.*,proptech_news.author.*,proptech_news.regions.*proptech_news.post_categories.*,proptech_news.tags.*`;
		const res = await fetch(apiUrl, {
			headers: {
				Authorization: `Bearer ${CMS_TOKEN}`,
				"Content-Type": "application/json",
			},
			next: { revalidate: 60 },
		});

		const response = await res.json();
		if (!res.ok) {
			Log.e(`Failed to fetch proptech news: ${res.status} ${res.statusText}`, { event: "general" });
			throw new Error("Failed to fetch proptech news from Directus");
		}

		const posts = response?.data?.proptech_news || [];
		const formattedPosts: MediaPosts = posts?.map((post: any) => transformToMediaPost(post));

		Log.i(`Successfully fetched ${formattedPosts.length} proptech news posts`, {
			event: "general",
		});
		return { posts: formattedPosts };
	} catch (error) {
		Log.e(`Error fetching proptech news: ${error}`, { event: "general" });
		throw new Error("Failed to fetch featured posts");
	}
};

/**
 * Retrieves posts from the Directus API
 *
 * @param queryParams - Optional query parameters for filtering the posts
 * @returns A promise that resolves to an object containing the retrieved posts and the total count
 */

export async function getPostsFromDirectus({
	page = 1,
	per_page = 50,
	type = "blog",
}: {
	page?: number;
	per_page?: number;
	type?: string;
}) {
	try {
		const limit = per_page;
		const offset = (page - 1) * limit;
		const filter = `&filter[type][_eq]=${type}`;
		const fields = "*,posts_categories.*,author.*,tags.*,regions.*";
		const apiUrl = `${CMS_URL}/items/Posts?fields=${fields}&limit=${limit}&offset=${offset}${filter}`;

		const res = await fetch(apiUrl, {
			headers: {
				Authorization: `Bearer ${CMS_TOKEN}`,
				"Content-Type": "application/json",
			},
			next: {
				revalidate: 30,
			},
		});

		if (!res.ok) {
			const errorText = await res.text();
			Log.e(`Failed to fetch posts: ${res.status} ${res.statusText} - ${errorText}`, {
				event: "general",
			});
			throw new Error(`Failed: ${res.status}`);
		}

		const response = await res.json();
		const posts = response?.data && response.data.length > 0 ? response.data : [];

		// Get total count
		const countApiUrl = `${CMS_URL}/items/Posts?aggregate[count]=*&filter[type][_eq]=${type}`;
		Log.d(`Making count API request to: ${countApiUrl}`, { event: "general" });

		const countRes = await fetch(countApiUrl, {
			headers: {
				Authorization: `Bearer ${CMS_TOKEN}`,
				"Content-Type": "application/json",
			},
		});

		let totalCount = 0;
		let totalPages = 0;

		if (!countRes.ok) {
			Log.e(`Directus count API error: ${countRes.status}`, { event: "general" });
		} else {
			const countData = await countRes.json();
			totalCount = countData?.data?.[0]?.count || 0;
			totalPages = Math.ceil(totalCount / limit);
		}

		const formattedPosts: MediaPosts = posts?.map((post: any) => transformToMediaPost(post));

		Log.i(
			`Successfully fetched ${formattedPosts.length} posts, Total: ${totalCount}, Pages: ${totalPages}`,
			{ event: "general" }
		);

		return {
			posts: formattedPosts,
			totalCount,
			totalPages,
		};
	} catch (error) {
		Log.e(`Error in getPostsFromDirectus: ${error}`, { event: "general" });
		throw error;
	}
}
export const getPostsByRegionsCategory = async (
	queryParams?: MediaQueryParams
): Promise<{
	posts: MediaPosts;
	totalCount: number;
}> => {
	try {
		const url = new URL(`${CMS_URL}/items/Posts`);
		url.searchParams.set(
			"fields",
			"*,posts_categories.*,posts_categories.Categories_id.*,author.*,tags.*,regions.Regions_id.*"
		);

		const filter: Record<string, any> = {};

		if (queryParams?.regionId) {
			filter["regions"] = {
				Regions_id: {
					id: { _eq: queryParams.regionId },
				},
			};
			Log.d(`Applied region filter: ${queryParams.regionId}`, { event: "general" });
		} else if (queryParams?.categoryId) {
			filter["posts_categories"] = {
				Categories_id: {
					id: { _eq: queryParams.categoryId },
				},
			};
			Log.d(`Applied category filter: ${queryParams.categoryId}`, { event: "general" });
		}

		if (Object.keys(filter).length > 0) {
			url.searchParams.set("filter", JSON.stringify(filter));
		}

		Log.d(`Making API request to: ${url.toString()}`, { event: "general" });

		const response = await fetch(url.toString(), {
			headers: {
				Authorization: `Bearer ${CMS_TOKEN}`,
				"Content-Type": "application/json",
			},
		});

		if (!response.ok) {
			Log.e(`HTTP error in getPostsByRegionsCategory: ${response.status}`, { event: "general" });
			throw new Error(`HTTP error! status: ${response.status}`);
		}

		const data = await response.json();
		Log.d(`API response received: ${JSON.stringify(data)}`, { event: "general" });

		const posts = data?.data || [];
		const totalCount = data?.meta?.total_count || posts.length;
		const formattedPosts: MediaPosts = posts?.map((post: any) => transformToMediaPost(post));

		Log.i(`Successfully fetched ${formattedPosts.length} posts by regions/category`, {
			event: "general",
		});

		return {
			posts: formattedPosts,
			totalCount,
		};
	} catch (error) {
		Log.e(`Error in getPostsByRegionsCategory: ${error}`, { event: "general" });
		return {
			posts: [],
			totalCount: 0,
		};
	}
};
/**
 * Retrieves featured posts from the Directus API
 *
 * @param isBlogPagePost - Optional boolean to filter posts for blog page
 * @returns A promise that resolves to an object containing the retrieved posts and the total count
 */

export async function getFeaturedPostsDirectus({ count = 3 }: { count?: number }) {
	try {
		const fields = [
			"*",
			"featured_posts.*",
			"featured_posts.featured_media.*",
			"featured_posts.post_tags.Tags_id.*",
			"featured_posts.post_categories.*",
			"featured_posts.regions.Regions_id.*",
			"featured_posts.author.*",
			"featured_posts.tags.*",
		].join(",");

		const apiUrl = `${CMS_URL}/items/news_landing_page?fields=${fields}&limit=${count}`;
		const res = await fetch(apiUrl, {
			headers: {
				Authorization: `Bearer ${CMS_TOKEN}`,
				"Content-Type": "application/json",
			},
			next: { revalidate: 30 },
		});

		if (!res.ok) {
			Log.e(`Failed to fetch featured posts: ${res.status} ${res.statusText}`, {
				event: "general",
			});
			throw new Error("Failed to fetch blog posts from Directus");
		}

		const response = await res.json();
		const posts = response?.data?.featured_posts ?? [];

		const formattedPosts: MediaPosts = posts.map((post: any, index: number) => {
			const transformedPost = transformToMediaPost(post);
			return {
				...transformedPost,
				is_featured_post_one: index === 0,
				is_featured_post_two: index === 1,
				is_featured_post_three: index === 2,
			};
		});

		Log.i(`Successfully fetched ${formattedPosts.length} featured posts`, { event: "general" });

		return {
			posts: formattedPosts,
			totalCount: formattedPosts.length,
		};
	} catch (error) {
		Log.e(`Error fetching featured posts: ${error}`, { event: "general" });
		throw new Error("Failed to fetch featured posts");
	}
}

/**
 * Sets the query parameters of a URL based on the provided `queryParams` object
 * Only non-null and non-undefined values in the `queryParams` object will be added as query parameters
 * The `processParamValue` function is used to format the parameter values before setting them in the URL
 *
 * @param url - The URL object to modify
 * @param queryParams - An object containing the query parameters to set
 * @returns The modified URL object with the updated query parameters
 */

export const getPopularPosts = async (
	queryParams?: MediaQueryParams
): Promise<{
	posts: MediaPosts;
	totalCount: number;
}> => {
	try {
		const fields = [
			"*",
			"popular_this_week.*",
			"popular_this_week.tags.*",
			"popular_this_week.post_categories.*",
			"popular_this_week.regions.*",
			"popular_this_week.author.*",
		].join(",");

		const { page, per_page } = queryParams || {};
		const offset = (Number(page) - 1) * Number(per_page);

		const apiUrl = `${CMS_URL}/items/news_landing_page?fields=${fields}&limit=${per_page}&offset=${offset}`;
		const response = await fetch(apiUrl, {
			headers: {
				Authorization: `Bearer ${CMS_TOKEN}`,
				"Content-Type": "application/json",
			},
			next: {
				revalidate: 30,
			},
		});

		if (!response.ok) {
			Log.e(`Failed to fetch popular posts: ${response.status} ${response.statusText}`, {
				event: "general",
			});
			throw new Error("Failed to fetch popular this week posts from Directus");
		}

		const data = await response.json();
		const popularThisWeekData = data?.data?.popular_this_week || [];
		const posts = popularThisWeekData && popularThisWeekData.length > 0 ? popularThisWeekData : [];

		const totalPages = response.headers.get("x-wp-totalpages") || "0";
		const totalCount = parseInt(totalPages) || posts.length;

		if (posts.length === 0) {
			Log.i("No popular posts found", { event: "general" });
			return {
				posts: [],
				totalCount: 0,
			};
		}

		const formattedPosts: MediaPosts = posts?.map((post: any) => transformToMediaPost(post));

		Log.i(`Successfully fetched ${formattedPosts.length} popular posts`, { event: "general" });

		return {
			posts: formattedPosts,
			totalCount,
		};
	} catch (error) {
		Log.e(`Error fetching popular posts: ${error}`, { event: "general" });
		return {
			posts: [],
			totalCount: 0,
		};
	}
};

export const setQueryParams = (url: URL, queryParams: MediaQueryParams) => {
	for (const paramKey in queryParams) {
		if (queryParams[paramKey]) {
			const paramValue = queryParams[paramKey];

			const formattedParamValue = processParamValue(paramValue);

			if (formattedParamValue != null) {
				url.searchParams.set(paramKey, formattedParamValue);
			}
		}
	}

	return url;
};

/**
 * Processes a media query parameter value and returns it as a string
 *
 * @param paramValue - The value of the media query parameter
 * @returns The processed value as a string, or null if the value cannot be processed
 */
export const processParamValue = (paramValue: MediaQueryParamValue): string | null => {
	if (Array.isArray(paramValue)) {
		return paramValue.join(",");
	}

	if (typeof paramValue === "number") {
		return paramValue.toString();
	}

	if (typeof paramValue === "string") {
		return paramValue;
	}

	if (typeof paramValue === "boolean") {
		return paramValue.toString();
	}

	return null;
};

/**
 * Retrieves posts from the media portal WP API
 *
 * @param queryParams - Optional query parameters for filtering the posts
 * @returns A promise that resolves to an object containing the retrieved posts and the total count
 */
export const getPosts = async (
	queryParams?: MediaQueryParams
): Promise<{
	posts: MediaPosts;
	totalCount: number;
}> => {
	try {
		let url = new URL(`${MEDIA_API_BASE_URL}/posts`);

		if (queryParams) {
			url = setQueryParams(url, queryParams);
		}

		const response = await fetch(url, {
			next: {
				tags: ["news-home"],
				revalidate: 3600, // 1 hour
			},
		});

		const data = await response.json();

		const posts = data && data?.length > 0 ? data : [];

		const totalPages = response.headers.get("x-wp-totalpages") || "";

		const totalCount = parseInt(totalPages);

		const authorIds = posts?.map((post: any) => post?.author);

		const uniqueAuthorIds = authorIds.filter(
			(id: number, index: number) => authorIds.indexOf(id) === index
		);

		const authors = await getAuthors(uniqueAuthorIds);

		const mediaIds = posts?.map((post: any) => post?.featured_media);

		const mediaImages = await getMediaImages(mediaIds, { per_page: mediaIds?.length || 0 });

		const formattedPosts: MediaPosts = posts?.map((post: any) => {
			const date = new Date(post?.date).toLocaleDateString();
			const author = authors.filter((author: any) => author.id === post.author)[0];
			const mediaImage = mediaImages.filter((media: any) => media.id === post.featured_media)[0];

			return {
				id: post?.id,
				thumbnailUrl: mediaImage?.source_url || "/news/default-blog-post-placeholder.png",
				title: post?.title?.rendered,
				authorName: author?.name,
				slug: post?.slug,
				date: date,
				date_gmt: post?.date_gmt,
				modified: post?.modified,
				modified_gmt: post?.modified_gmt,
				categories: post?.categories,
				tags: post?.tags,
				regions: post?.regions,
				is_blog_page_post: post?.is_blog_page_post,
				is_featured_post_one: post?.is_featured_post_one,
				is_featured_post_two: post?.is_featured_post_two,
				is_featured_post_three: post?.is_featured_post_three,
			};
		});

		return {
			posts: formattedPosts,
			totalCount,
		};
	} catch (error) {
		console.log("Error while fetching posts from media portal WP API");

		console.log(error);

		return {
			posts: [],
			totalCount: 0,
		};
	}
};

/**
 * Retrieves authors from the media portal WP API based on the provided author IDs.
 * @param authorIds - An array of author IDs.
 * @returns A Promise that resolves to an array of authors.
 */
export const getAuthors = async (authorIds: number[]) => {
	try {
		const url = new URL(`${MEDIA_API_BASE_URL}/users`);

		const userIds = authorIds?.join(",");

		url.searchParams.set("include", userIds);

		const response = await fetch(url);

		const authors = await response.json();

		return authors;
	} catch {
		console.log("Error while fetching authors from media portal WP API");

		return [];
	}
};

/**
 * Retrieves the author information from the media portal WP API
 * @param authorId - The ID of the author
 * @returns A Promise that resolves to an array of author objects
 */
export const getAuthor = async (authorId: number) => {
	try {
		const url = new URL(`${MEDIA_API_BASE_URL}/users/${authorId.toString()}`);

		const response = await fetch(url);

		const authors = await response.json();

		return authors;
	} catch {
		console.log("Error while fetching author from media portal WP API");

		return [];
	}
};

/**
 * Retrieves media images from the media portal WP API based on the provided media IDs
 * @param mediaIds - An array of media IDs to retrieve images for
 * @returns A Promise that resolves to an array of media authors
 */
export const getMediaImages = async (mediaIds: number[], queryParams?: MediaQueryParams) => {
	try {
		let url = new URL(`${MEDIA_API_BASE_URL}/media`);

		if (queryParams) {
			url = setQueryParams(url, queryParams);
		}

		const formattedMediaIds = mediaIds?.join(",");

		url.searchParams.set("include", formattedMediaIds);

		const response = await fetch(url);

		const authors = await response.json();

		return authors;
	} catch {
		console.log("Error while fetching media images from media portal WP API");

		return [];
	}
};

/**
 * Retrieves a media image from the media portal WP API
 * @param mediaId - The ID of the media image to retrieve
 * @returns A Promise that resolves to the retrieved media image data
 */
export const getMediaImage = async (mediaId: number) => {
	try {
		const url = new URL(`${MEDIA_API_BASE_URL}/media/${mediaId.toString()}`);

		const response = await fetch(url);

		const authors = await response.json();

		return authors;
	} catch {
		console.log("Error while fetching media image from media portal WP API");

		return [];
	}
};

/**
 * Retrieves the list of regions from the media portal WP API
 * @param queryParams Optional query parameters to filter the regions
 * @returns A promise that resolves to an array of formatted MediaRegion objects
 */
export const getRegions = async (queryParams?: MediaQueryParams): Promise<MediaRegion[]> => {
	try {
		const url = new URL(`${CMS_URL}/items/Regions`);
		const filter: Record<string, any> = {};

		if (queryParams?.slug) {
			filter["slug"] = { _in: [queryParams.slug] };
		}

		if (Object.keys(filter).length > 0) {
			url.searchParams.set("filter", JSON.stringify(filter));
		}

		const response = await fetch(url, {
			headers: {
				Authorization: `Bearer ${CMS_TOKEN}`,
				"Content-Type": "application/json",
			},
		});

		if (!response.ok) {
			throw new Error(`HTTP error! status: ${response.status}`);
		}

		const regions = await response.json();
		const formattedRegions: MediaRegion[] =
			regions?.data?.map((region: any) => {
				return {
					id: region.id,
					name: region.name,
					slug: region.slug,
				};
			}) || [];

		return formattedRegions;
	} catch (error) {
		console.error("Error while fetching regions from media portal API:", error);
		return [];
	}
};

/**
 * Retrieves the media categories from the media portal WP API
 *
 * @param queryParams - Optional query parameters to filter the categories
 * @returns A promise that resolves to an array of formatted media categories
 */
export const getCategories = async (queryParams?: MediaQueryParams): Promise<MediaCategory[]> => {
	try {
		const url = new URL(`${CMS_URL}/items/Categories`);

		const filter: Record<string, any> = {};

		if (queryParams?.slug) {
			filter["slug"] = { _in: [queryParams.slug] };
		}

		if (Object.keys(filter).length > 0) {
			url.searchParams.set("filter", JSON.stringify(filter));
		}

		const response = await fetch(url, {
			headers: {
				Authorization: `Bearer ${CMS_TOKEN}`,
				"Content-Type": "application/json",
			},
			next: { revalidate: 60 },
		});

		const categories = await response.json();

		const formattedCategories: MediaCategory[] = categories.data.map((category: any) => {
			return {
				id: category.id,
				name: category.name,
				slug: category.slug,
			};
		});

		return formattedCategories;
	} catch (error) {
		console.log("Error while fetching categories from media portal WP API");
		console.log(error);
		return [];
	}
};

/**
 * Retrieves the tags from the media portal WP API
 *
 * @param queryParams - Optional query parameters to filter the tags
 * @returns A promise that resolves to an array of MediaTag objects
 */
export const getTags = async (queryParams?: MediaQueryParams): Promise<MediaTag[]> => {
	try {
		let url = new URL(`${MEDIA_API_BASE_URL}/tags`);

		if (queryParams) {
			url = setQueryParams(url, queryParams);
		}

		const response = await fetch(url);

		const tags = await response.json();

		const formattedTags: MediaCategory[] = tags.map((tag: any) => {
			return {
				id: tag.id,
				name: tag.name,
				slug: tag.slug,
			};
		});

		return formattedTags;
	} catch {
		console.log("Error while fetching tags from media portal WP API");

		return [];
	}
};
